<template>
    <section id="tab-receive-packages" class="receive-packages">
        <!-- Tracking Number -->
        <div class="receive-packages__input-row">
            <v-text-field
                id="inputPackageTrackingNumber"
                ref="packageTrackingNumber"
                v-model="models.trackingNumber"
                label="Tracking Number"
                type="text"
                dense
                outlined
                hideDetails
                @keydown.enter.native="trackingNumberValidationHandler( 'enter' )"
                @blur="trackingNumberValidationHandler( 'blur' )"
            />
        </div>

        <!-- Recipient -->
        <div class="receive-packages__input-row">
            <AutocompleteSuggestCustomers
                id="inputPackageRecipient"
                ref="packageRecipient"
                label="Recipient*"
                :rules="[ hasRecipient ]"
                :value="models.recipient"
                @input="( option ) => setRecipient( option )"
            />
        </div>

        <!-- Sender -->
        <div class="receive-packages__input-row">
            <AutocompleteSuggestSender
                id="inputPackageSender"
                ref="packageSender"
                allowCreate
                :value="models.sender"
                @input="( sender ) => setSender( sender )"
            />
        </div>

        <!-- Transaction Notes -->
        <div class="receive-packages__input-row--full">
            <v-textarea
                id="inputPackageNotes"
                ref="packageNotes"
                v-model="models.transactionNotes"
                outlined
                dense
                hideDetails=""
                label="Transaction Notes"
                type="text"
                @keydown.enter.native="focusAddButton()"
                @keydown.tab.native="focusAddButton()"
            />
            <button
                type="button"
                role="button"
                class="transaction-button"
                @click="displayPictureModal()"
            >
                <MaterialDesignIcon
                    :className="displayCamera"
                    pxSize="24"
                />
            </button>
        </div>

        <!-- Button Group -->
        <div class="receive-packages__actions">
            <ClButton
                ref="buttonAdd"
                :class="{ 'is-loading': receiveInFlight }"
                :disabled="receiveInFlight || !hasRequiredFields"
                :loading="receiveInFlight"
                @click="nextButtonHandler()"
            >
                Add
            </ClButton>
        </div>

        <!-- Picture Modal -->
        <ModalPictureSelection
            v-model="showPictureModal"
            :image="bgImageData"
            @updatePicture="( data ) => updatePicture( data )"
            @clearPicture="clearPicture()"
            @close="hidePictureModal"
        />
    </section>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import DateUtil from '@/utils/DateUtil';

// Components
import MaterialDesignIcon from '@/components/base/MaterialDesignIcon';
import AutocompleteSuggestCustomers from '@/components/AutocompleteSuggestCustomers';
import AutocompleteSuggestSender from '@/components/AutocompleteSuggestSender';
import ModalPictureSelection from '@/components/modal/ModalPictureSelection';

// Defaults
const defaults = {
    models: () => ( {
        recipient: null,
        sender: null,
        trackingNumber: null,
        transactionNotes: null,
    } ),
};

export default {
    name: 'TabPackages',
    components: {
        AutocompleteSuggestSender,
        MaterialDesignIcon,
        AutocompleteSuggestCustomers,
        ModalPictureSelection,
    },
    props: {
        receiveDate: {
            type: Date,
            required: false,
            default: DateUtil.now(),
        },
        carrier: {
            type: Object,
            required: true,
        },
        receiveInFlight: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            models: defaults.models(),
            showPictureModal: false,
            Data: [],
            hash: [],
            photos: [],
            bgImageData: [],
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
        } ),

        /** @returns { Boolean } */
        hasRecipient() {
            return !!this.models.recipient
                && ( Object.values( this.models.recipient ).length > 0 );
        },
        /** @returns { Boolean } */
        hasSender() {
            return !!this.models.sender
                && ( Object.values( this.models.sender ).length > 0 );
        },
        /** @returns { Boolean } */
        hasCarrier() {
            return ( Object.values( this.carrier ).length > 0 );
        },
        /** @returns { Boolean } */
        hasRequiredFields() {
            return this.hasCarrier && this.hasRecipient;
        },
        hasPicture() {
            return this.bgImageData.length;
        },
        displayCamera() {
            return ( this.hasPicture ? 'camera' : 'camera-outline' );
        },
        displayPhotoCount() {
            return this.bgImageData.length;
        },
    },
    watch: {
        carrier() {
            if ( this.carrier.id === 5 ) {
                this.models.sender = 'Amazon';
            } else {
                this.models.sender = '';
            }
        },
    },
    mounted() {
        this.$emit( 'defaultPackageCarrier' );
        this.initialFocus();
    },
    methods: {
        initialFocus() {
            this.$refs.packageTrackingNumber.focus();
        },

        focusAddButton() {
            if ( this.hasRequiredFields ) {
                this.$refs.buttonAdd.$el.focus();
            }
        },

        setRecipient( option ) {
            this.models.recipient = option || {};
            if ( this.hasRecipient ) {
                this.$refs.packageSender.$refs.autocomplete.focus();
            }
        },

        setSender( sender ) {
            this.models.sender = sender;
            if ( this.hasSender ) {
                this.focusAddButton();
            }
        },

        displayPictureModal() {
            this.showPictureModal = true;
        },

        hidePictureModal() {
            this.showPictureModal = false;
        },

        updatePicture( data ) {
            this.bgImageData = data.image;
            this.hash = data.hash;
            this.hidePictureModal();
            this.focusAddButton();
        },

        clearPicture() {
            this.bgImageData = [];
            this.hash = [];
        },

        async validateTrackingNumber() {
            // 1Z 9999 9999 9999 9999
            try {
                const { data } = await LogisticsApi.validateTrackingNumber( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    trackingNumber: this.models.trackingNumber,
                } );
                this.$emit( 'trackingNumberValidation', {
                    carrierFriendly: data.mailCarrier,
                    trackingNumber: data.trackingNumber,
                    valid: data.valid,
                } );
            } catch ( error ) {
                console.error( 'Error validating tracking number: ', error );
            }
        },
        async trackingNumberValidationHandler( n ) {
            /*
            *   The parameter was passed to this function so we wouldn't call the
            *   validateTrackingNumber more than once.
            *
            *   If you hit enter or tab it will send 2 events, one for the enter or tab
            *   and one for the blur.
            *
            *   If you use the mouse to select the name in the dropdown, it will only
            *   send the blur.
            *
            *   Also moved the focus to the receive field inside the if statement, because
            *   when you come into the screen and then go select the carrier, it will not
            *   move you to the Recipient field and bypass the Tracking Number. It leaves
            *   you on the carrier field so you can hit tab and get to the tracking field
            */
            setTimeout( () => {
                const focusedElement = document.activeElement;
                const inputs = [ 'input', 'select', 'button', 'textarea' ];
                if (
                    this.models.trackingNumber && (
                        n !== 'blur'
                        || !focusedElement
                        || inputs.indexOf( focusedElement.tagName.toLowerCase() ) === -1 )
                ) {
                    this.$refs.packageRecipient.$refs.autocomplete.focus();
                }
            }, 1 );
            if ( this.models.trackingNumber && ( n === 'blur' ) ) {
                await this.validateTrackingNumber();
                // Use timeout to delay examination of activeElement until after blur/focus
                // events have been processed.
                // Only set focus if the user did not click on another input field.
            }
        },

        /** @returns { Boolean } */
        canProceedToNextPackage() {
            if ( !this.hasRequiredFields ) {
                this.$store.dispatch( 'snackbar', {
                    message: 'Select a Carrier and fill out the Recipient',
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                return false;
            }
            return true;
        },
        addNewPackage() {
            this.photos = [];
            if ( this.bgImageData.length > 0 ) {
                this.photos.push( { checkSum: this.hash[ 0 ].toString(), data: this.bgImageData[ 0 ].toString() } );
            }
            this.$emit( 'newPackage', {
                date: this.receiveDate,
                carrier: this.carrier,
                trackingNumber: this.models.trackingNumber,
                recipient: this.models.recipient,
                sender: this.models.sender,
                notes: this.models.transactionNotes,
                photos: this.photos,
            } );
            this.initialFocus();
        },
        nextButtonHandler() {
            if ( this.canProceedToNextPackage() ) {
                this.addNewPackage();
                this.models = defaults.models();
                if ( this.carrier.id === 5 ) {
                    this.models.sender = 'Amazon';
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/sass";

.receive-packages {
    display: grid;
    grid-template-columns: auto 2.5rem;
    row-gap: 1rem;

    // All the items take just the first column
    & > * {
        grid-column: 1 / 1;
    }
    &__input-row--full {
        grid-column: 1 / -1;
        display: grid;

        // Match the parent template columns
        grid-template-columns: auto 2.5rem;

        // Cleaner upcoming solution that pulls from the parent grid
        // Only supported in firefox currently
        // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout/Subgrid
        grid-template-columns: subgrid;
    }
    &__actions {
        justify-self: end;
    }
}
.transaction-button {
    color: $colors-grey-darken-1;
    align-self: flex-start;
    justify-self: flex-end;
}

.transaction {
    display: flex;
    align-items: flex-start;
}

</style>
